import React, { useCallback, useId } from "react";
import { styled } from "@mui/material/styles";
import { Box, MenuItem, Select, Typography } from "@mui/material";
import {
  NODE_UI,
  stopPropagation,
  getCanvasSurfaceProps,
  getDialogSurfaceZIndex,
} from "./nodeUiStyles";

function SelectOptionCard({ option }) {
  const Icon = option?.icon;
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 0.25 }}>
      {Icon ? (
        <Icon
          sx={{
            fontSize: 18,
            mt: 0.15,
            color: "text.secondary",
            flexShrink: 0,
          }}
        />
      ) : null}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: NODE_UI.fontSize.value,
            fontWeight: 600,
            lineHeight: 1.3,
            color: "text.primary",
          }}
        >
          {option.label}
        </Typography>
        {option.description ? (
          <Typography
            sx={{
              fontSize: NODE_UI.fontSize.caption,
              fontWeight: NODE_UI.fontWeight.value,
              lineHeight: 1.35,
              color: "text.secondary",
              mt: 0.25,
            }}
          >
            {option.description}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

const renderDescriptiveOption = (option) => <SelectOptionCard option={option} />;

const renderDescriptiveValue = (selected, options) => {
  const option = options.find((o) => o.value === selected);
  if (!option) return selected;
  const Icon = option.icon;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
      {Icon ? <Icon sx={{ fontSize: 16, color: "text.secondary", flexShrink: 0 }} /> : null}
      <Typography
        sx={{
          fontSize: NODE_UI.fontSize.value,
          fontWeight: 500,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {option.label}
      </Typography>
    </Box>
  );
};

/**
 * NodeSelect — standard dropdown for choosing from a fixed list of options.
 * Styled for on-canvas node config: compact, monotone, shadcn-inspired.
 *
 * Usage:
 *   <NodeSelect
 *     label="Output Format"
 *     value={format}
 *     onChange={(val) => setFormat(val)}
 *     options={[
 *       { value: 'json', label: 'JSON' },
 *       { value: 'csv', label: 'CSV' },
 *     ]}
 *   />
 */

const StyledSelect = styled(Select)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.value,
  borderRadius: NODE_UI.borderRadiusSm,
  backgroundColor: theme.palette.background.default,
  "& fieldset": {
    borderColor: theme.palette.divider,
  },
  "&:hover fieldset": {
    borderColor: theme.palette.text.secondary,
  },
  "&.Mui-focused fieldset": {
    borderColor: theme.palette.text.primary,
    borderWidth: "1px",
  },
  "& .MuiSelect-select": {
    padding: NODE_UI.input.padding,
    fontSize: NODE_UI.fontSize.value,
  },
}));

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: "4px",
  userSelect: "none",
}));

const StyledMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== "descriptive",
})(({ theme, descriptive }) => ({
  fontSize: NODE_UI.fontSize.value,
  padding: descriptive ? "8px 12px" : "6px 12px",
  whiteSpace: descriptive ? "normal" : "nowrap",
  alignItems: descriptive ? "flex-start" : "center",
  "&.Mui-selected": {
    backgroundColor: theme.palette.action.selected,
  },
  "&.Mui-selected:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const NodeSelect = React.memo(function NodeSelect({
  label,
  value,
  onChange,
  options = [],
  placeholder,
  disabled = false,
  fullWidth = true,
  size = "small",
  sx,
  renderValue,
  renderOption,
  displayEmpty = false,
  /** Menu items show icon + label + description cards (options need `icon` + `description`). */
  descriptive = false,
  /**
   * React Flow nodes are positioned with CSS transforms. Use this on canvas
   * nodes: keep the menu portaled (correct screen position) but mark the popper
   * nodrag/nopan so clicks register.
   */
  canvas = false,
  /** Raise portaled menu above MUI Dialog (required when dialog uses zIndex > modal). */
  inDialog = false,
}) {
  const handleChange = useCallback(
    (e) => {
      onChange?.(e.target.value);
    },
    [onChange]
  );
  const handleOpen = useCallback((e) => {
    e.stopPropagation();
  }, []);
  const generatedId = useId();
  const labelId = `node-select-${generatedId}-label`;
  const canvasSurfaceProps = getCanvasSurfaceProps(canvas);
  const resolvedRenderOption = renderOption || (descriptive ? renderDescriptiveOption : undefined);
  const resolvedRenderValue =
    renderValue ||
    (descriptive
      ? (selected) => renderDescriptiveValue(selected, options)
      : placeholder
        ? (selected) => {
            if (!selected && selected !== 0) {
              return (
                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: NODE_UI.fontSize.value,
                  }}
                >
                  {placeholder}
                </Typography>
              );
            }
            const option = options.find((o) => o.value === selected);
            return option?.label ?? selected;
          }
        : undefined);
  const menuProps = {
    // On canvas the menu must not lock body scroll (it fights the pannable surface); off canvas
    // we keep MUI's default scroll lock.
    disableScrollLock: canvas,
    ...(canvas ? { style: { zIndex: 1500 } } : {}),
    PaperProps: {
      ...canvasSurfaceProps,
      sx: (theme) => ({
        borderRadius: NODE_UI.borderRadiusSm,
        boxShadow: NODE_UI.shadow,
        border: `1px solid ${theme.palette.divider}`,
        marginTop: "2px",
        ...(descriptive ? { minWidth: 300 } : {}),
        ...(inDialog ? { zIndex: getDialogSurfaceZIndex(theme) } : {}),
      }),
    },
    MenuListProps: {
      onMouseDown: stopPropagation,
      onClick: stopPropagation,
      onPointerDown: stopPropagation,
    },
  };

  return (
    <div
      className={canvas ? "nodrag nopan nowheel" : undefined}
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
      onPointerDown={stopPropagation}
    >
      {label && <Label id={labelId}>{label}</Label>}
      <StyledSelect
        labelId={label ? labelId : undefined}
        inputProps={
          !label && placeholder ? { "aria-label": placeholder } : undefined
        }
        value={value ?? ""}
        onChange={handleChange}
        onOpen={handleOpen}
        variant="outlined"
        displayEmpty={displayEmpty || !!placeholder}
        renderValue={resolvedRenderValue}
        MenuProps={menuProps}
        fullWidth={fullWidth}
        size={size}
        disabled={disabled}
        sx={sx}
      >
        {options.map((opt) => (
          <StyledMenuItem
            key={opt.value}
            value={opt.value}
            disabled={opt.disabled}
            descriptive={descriptive}
          >
            {resolvedRenderOption ? resolvedRenderOption(opt) : opt.label}
          </StyledMenuItem>
        ))}
      </StyledSelect>
    </div>
  );
});

export default NodeSelect;
